import type { CSSProperties } from "react";

import Pill from "@buildpy/ui/pill";
import SectionCard from "@buildpy/ui/section-card";
import { SnapshotItem } from "@buildpy/core/modules/quote/types";
import { formatWon } from "@buildpy/core/lib/format";
import Money from "@/components/money";
import { cn } from "@/lib/utils";
import { formatQty } from "@/modules/quote/ui/format";

// 수량 × 단가 — 단가가 0 인 쪽은 빼고, SET 부모는 단가가 없어 단위만 보인다(인쇄 상세와 같다).
export const qtyLine = (item: SnapshotItem): string => {
  if (item.children.length > 0) return item.unit;
  const prices = [
    item.material_unit_price > 0 ? `자재 ${formatWon(item.material_unit_price)}` : "",
    item.labor_unit_price > 0 ? `노무 ${formatWon(item.labor_unit_price)}` : "",
  ].filter(Boolean);
  const qty = `${formatQty(item.qty)} ${item.unit}`.trim();
  return prices.length > 0 ? `${qty} × ${prices.join(" + ")}` : qty;
};

const flatChildren = (items: SnapshotItem[], depth = 1): { item: SnapshotItem; depth: number }[] =>
  items.flatMap((item) => [{ item, depth }, ...flatChildren(item.children, depth + 1)]);

// 폰 견적 항목 카드(시안 1-b) — 작업내역 · 범위 · 수량×단가·금액 · 비고 · 세트 하위 └ 줄. 고객 견적서와 사내 견적 상세가 같이 쓴다.
const QuoteItemCard = ({ item, testId }: { item: SnapshotItem; testId?: string }) => {
  const isSet = item.children.length > 0;
  return (
    <SectionCard as="article" className="overflow-hidden" data-testid={testId} padding="none">
      <div className="flex flex-col gap-1.5 px-4 py-3.5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 break-words font-semibold leading-snug">{item.item}</div>
          {(isSet || item.space_label) && (
            <span className="flex shrink-0 gap-1">
              {item.space_label && <Pill>{item.space_label}</Pill>}
              {isSet && <Pill>세트</Pill>}
            </span>
          )}
        </div>
        {item.scope && <div className="break-words text-sm text-muted-foreground">{item.scope}</div>}
        <div className="mt-1 flex items-center justify-between gap-3">
          <span className="min-w-0 text-sm text-muted-foreground tabular-nums">{qtyLine(item)}</span>
          <Money className="font-semibold" value={item.amount} />
        </div>
        {item.note && <div className="break-words rounded-sm bg-muted px-2 py-1.5 text-xs text-muted-foreground">비고 · {item.note}</div>}
      </div>
      {isSet && (
        <div className="flex flex-col gap-1.5 border-t border-border bg-muted py-2 pl-7 pr-4">
          {flatChildren(item.children).map(({ item: child, depth }, i) => (
            <div key={`${depth}-${i}-${child.item}`} className={cn("flex justify-between gap-3 text-sm text-muted-foreground", depth > 1 && "pl-(--pl)")} style={depth > 1 ? ({ "--pl": `${(depth - 1) * 12}px` } as CSSProperties) : undefined}>
              <span className="min-w-0">└ {child.item}</span>
              {child.amount > 0 ? <Money value={child.amount} /> : <span className="whitespace-nowrap">포함</span>}
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
};

export default QuoteItemCard;
